import { BufferAttribute, BufferGeometry, Group, Line, LineSegments, Mesh, Points, type Object3D } from 'three'

/**
 * Zero-copy transfer of parsed geometry across the file-parser worker boundary.
 *
 * The previous implementation serialized the worker output with `Object3D.toJSON()`
 * + `JSON.stringify`, which turns every float into ~15-18 ASCII characters, copies
 * the result (strings are cloned, not transferred) and hits V8's ~512 MB max string
 * length on large meshes — the practical ceiling that made multi-GB files crash.
 *
 * Instead we flatten the object tree into a plain structure that keeps the geometry
 * as its raw TypedArrays, and hand the underlying ArrayBuffers to `postMessage` as
 * transferables. Nothing is copied and there is no intermediate string.
 *
 * Object transforms (position/quaternion/scale) ARE preserved: `ObjectLoader.parse()`
 * restored them from the serialized matrix, and some formats (notably 3MF build items
 * and components) carry non-identity transforms on the parsed objects.
 */

type TransferableTypedArray = Float32Array | Uint32Array | Uint16Array | Uint8Array | Int32Array

export type SerializedAttribute = {
  array: TransferableTypedArray
  itemSize: number
}

export type SerializedGeometry = {
  position?: SerializedAttribute
  normal?: SerializedAttribute
  uv?: SerializedAttribute
  index?: SerializedAttribute
}

export type SerializedObjectType = 'Group' | 'Mesh' | 'Line' | 'LineSegments' | 'Points'

export type SerializedObject = {
  type: SerializedObjectType
  name?: string
  userData?: Record<string, unknown>
  position?: [number, number, number]
  quaternion?: [number, number, number, number]
  scale?: [number, number, number]
  geometry?: SerializedGeometry
  children?: SerializedObject[]
}

// ============================================================================
// Serialization (worker side)
// ============================================================================

const ATTRIBUTE_NAMES = ['position', 'normal', 'uv'] as const

function serializeAttribute(
  attribute: BufferAttribute,
  transfer: ArrayBuffer[],
  seen: Set<ArrayBuffer>,
): SerializedAttribute {
  let array = attribute.array as TransferableTypedArray

  // We may only transfer a buffer we fully own. If this TypedArray is a partial
  // view into a larger buffer, transferring that buffer would either drag along
  // sibling data or neuter it for another attribute, so copy it tight first.
  if (array.byteOffset !== 0 || array.byteLength !== array.buffer.byteLength) {
    array = array.slice() as TransferableTypedArray
  }

  // Geometry attributes are always ArrayBuffer-backed (never SharedArrayBuffer).
  const buffer = array.buffer as ArrayBuffer

  // A buffer may only appear once in the transfer list — postMessage throws
  // DataCloneError on a duplicate. Two attributes sharing one buffer is fine:
  // structured clone rebuilds both views over the single transferred buffer.
  if (!seen.has(buffer)) {
    seen.add(buffer)
    transfer.push(buffer)
  }

  return { array, itemSize: attribute.itemSize }
}

function serializeGeometry(
  geometry: BufferGeometry,
  transfer: ArrayBuffer[],
  seen: Set<ArrayBuffer>,
): SerializedGeometry {
  const serialized: SerializedGeometry = {}

  for (const name of ATTRIBUTE_NAMES) {
    const attribute = geometry.getAttribute(name) as BufferAttribute | undefined
    if (attribute) {
      serialized[name] = serializeAttribute(attribute, transfer, seen)
    }
  }

  if (geometry.index) {
    serialized.index = serializeAttribute(geometry.index, transfer, seen)
  }

  return serialized
}

function serializedType(object: Object3D): SerializedObjectType {
  switch (object.type) {
    case 'Mesh':
    case 'Line':
    case 'LineSegments':
    case 'Points':
      return object.type
    default:
      return 'Group'
  }
}

function serializeObject(object: Object3D, transfer: ArrayBuffer[], seen: Set<ArrayBuffer>): SerializedObject {
  const geometry = (object as Mesh | Line).geometry as BufferGeometry | undefined

  const serialized: SerializedObject = { type: serializedType(object) }

  if (object.name) {
    serialized.name = object.name
  }
  if (object.userData && Object.keys(object.userData).length > 0) {
    serialized.userData = object.userData
  }

  // Preserve transforms — 3MF build items / components carry non-identity ones.
  const { position, quaternion, scale } = object
  if (position.x !== 0 || position.y !== 0 || position.z !== 0) {
    serialized.position = [position.x, position.y, position.z]
  }
  if (quaternion.x !== 0 || quaternion.y !== 0 || quaternion.z !== 0 || quaternion.w !== 1) {
    serialized.quaternion = [quaternion.x, quaternion.y, quaternion.z, quaternion.w]
  }
  if (scale.x !== 1 || scale.y !== 1 || scale.z !== 1) {
    serialized.scale = [scale.x, scale.y, scale.z]
  }

  if (geometry) {
    serialized.geometry = serializeGeometry(geometry, transfer, seen)
  }
  if (object.children.length > 0) {
    serialized.children = object.children.map(child => serializeObject(child, transfer, seen))
  }

  return serialized
}

/**
 * Flatten Three.js objects into a transfer-friendly structure.
 *
 * Materials are intentionally dropped: every consumer reassigns its own material
 * after parsing. Only geometry buffers, transforms, names, userData and hierarchy
 * are kept.
 *
 * @returns the serialized tree plus the ArrayBuffers to pass as `postMessage` transferables.
 */
export function serializeObjectsForTransfer(objects: Object3D[]): {
  objects: SerializedObject[]
  transfer: ArrayBuffer[]
} {
  const transfer: ArrayBuffer[] = []
  const seen = new Set<ArrayBuffer>()
  const serialized = objects.map(object => serializeObject(object, transfer, seen))
  return { objects: serialized, transfer }
}

// ============================================================================
// Deserialization (main-thread side)
// ============================================================================

function deserializeGeometry(serialized: SerializedGeometry): BufferGeometry {
  const geometry = new BufferGeometry()

  if (serialized.position) {
    geometry.setAttribute('position', new BufferAttribute(serialized.position.array, serialized.position.itemSize))
  }
  if (serialized.normal) {
    geometry.setAttribute('normal', new BufferAttribute(serialized.normal.array, serialized.normal.itemSize))
  }
  if (serialized.uv) {
    geometry.setAttribute('uv', new BufferAttribute(serialized.uv.array, serialized.uv.itemSize))
  }
  if (serialized.index) {
    geometry.setIndex(new BufferAttribute(serialized.index.array, serialized.index.itemSize))
  }

  return geometry
}

function createObject(serialized: SerializedObject): Object3D {
  const geometry = serialized.geometry ? deserializeGeometry(serialized.geometry) : undefined

  switch (serialized.type) {
    case 'Mesh':
      return new Mesh(geometry)
    case 'Line':
      return new Line(geometry)
    case 'LineSegments':
      return new LineSegments(geometry)
    case 'Points':
      return new Points(geometry)
    default:
      // A geometry-bearing object must never silently become an empty Group.
      return geometry ? new Mesh(geometry) : new Group()
  }
}

function deserializeObject(serialized: SerializedObject): Object3D {
  const object = createObject(serialized)

  if (serialized.name) {
    object.name = serialized.name
  }
  if (serialized.userData) {
    object.userData = serialized.userData
  }
  if (serialized.position) {
    object.position.fromArray(serialized.position)
  }
  if (serialized.quaternion) {
    object.quaternion.fromArray(serialized.quaternion)
  }
  if (serialized.scale) {
    object.scale.fromArray(serialized.scale)
  }

  serialized.children?.forEach(child => object.add(deserializeObject(child)))

  return object
}

/**
 * Rebuild real Three.js objects from a structure produced by
 * {@link serializeObjectsForTransfer}. The geometry TypedArrays are reused as-is
 * (their buffers were transferred), so this stays zero-copy.
 */
export function deserializeTransferredObjects(objects: SerializedObject[]): Object3D[] {
  return objects.map(deserializeObject)
}
